import type { AthletePaymentAssignment } from '../object-actions/types/types';
import {
  hasAnyEntitlement,
  hasAnyLessonEntitlement,
  hasEntitlement,
  lessonEntitlementId,
  TOTAL_PROGRAM_SESSIONS,
} from './entitlements';
import type { EntitlementId, EntitlementsMap } from './types';

export const LESSON_PLAN_ENTITLEMENTS: EntitlementId[] = Array.from(
  { length: TOTAL_PROGRAM_SESSIONS },
  (_, index) => lessonEntitlementId(index + 1),
);

/**
 * Coach content purposes map 1:1 onto entitlement ids, except lesson plans,
 * where owning any single session is enough to open the plan.
 */
const PURPOSE_ENTITLEMENT: Record<string, EntitlementId | EntitlementId[]> = {
  feedback_report: 'feedback_report',
  talking_points: 'talking_points',
  curriculum: 'curriculum',
  growth_report_week_4: 'growth_report_week_4',
  growth_report_week_8: 'growth_report_week_8',
  growth_report_week_12: 'growth_report_week_12',
  lesson_plan: LESSON_PLAN_ENTITLEMENTS,
};

export function entitlementForPurpose(purpose: string): EntitlementId | EntitlementId[] | null {
  return PURPOSE_ENTITLEMENT[purpose] ?? null;
}

export function hasRequiredEntitlement(
  entitlements: EntitlementsMap | null | undefined,
  required: EntitlementId | EntitlementId[],
): boolean {
  if (Array.isArray(required)) {
    return hasAnyEntitlement(entitlements, required);
  }
  return hasEntitlement(entitlements, required);
}

export function assignmentHasPurposeEntitlement(
  assignment: AthletePaymentAssignment,
  purpose: string,
): boolean {
  const required = entitlementForPurpose(purpose);
  if (!required) {
    return true;
  }
  return hasRequiredEntitlement(assignment.entitlements, required);
}

export function canAccessPreAssessment(assignment: AthletePaymentAssignment): boolean {
  return hasEntitlement(assignment.entitlements, 'pre_assessment');
}

export function canAccessLessonPlan(entitlements: EntitlementsMap | null | undefined): boolean {
  return hasAnyLessonEntitlement(entitlements);
}

/** Per-session gate; mirrors LessonPlanSessionPermission on the server. */
export function canAccessLessonSession(
  entitlements: EntitlementsMap | null | undefined,
  sessionNumber: number,
): boolean {
  return hasEntitlement(entitlements, lessonEntitlementId(sessionNumber));
}
